import { useContext, useMemo, useCallback, useState, useEffect } from "react";
import { MessagesContext } from "../context/MessagesContext";
import { ContactsContext } from "../context/ContactsContext";
import { toTimestamp } from "../utils/dateutils";
import { IMessage } from "../types/message";
import { useBookmark } from "./useBookmark";
import { IContact } from "../types/contacts";

export const useChats = () => {
  const msgCtx = useContext(MessagesContext);
  const contactCtx = useContext(ContactsContext);
  const { favourites } = useBookmark(); // ⭐ vem pronto do hook

  if (!msgCtx) throw new Error("useChats must be inside <MessagesProvider>");
  if (!contactCtx)
    throw new Error("useChats must be inside <ContactsProvider>");

  const { messages } = msgCtx;
  const { contacts } = contactCtx;

  // 👉 contatos favoritados (bookmarks de contato)
  const bookmarkContact = favourites ?? [];

  const getMessagesByContact = useCallback(
    (contactId: number) => {
      return messages
        .filter((m: IMessage) => m.idContact === contactId)
        .sort((a, b) => toTimestamp(a) - toTimestamp(b)); // ASC
    },
    [messages],
  );

  const getLastMessage = useCallback(
    (contactId: number) => {
      const msgs = getMessagesByContact(contactId);
      if (msgs.length === 0) return null;

      return msgs.reduce((a, b) => (toTimestamp(b) > toTimestamp(a) ? b : a));
    },
    [getMessagesByContact],
  );

  const getUnreadCount = useCallback(
    (contactId: number) => {
      const count = messages.filter(
        m =>
          m.idContact === contactId &&
          m.isFromContact === true &&
          m.isRead === 0,
      ).length;

      // console.log("UNREAD", contactId, count);

      return count;
    },
    [messages],
  );

  const chats = useMemo(() => {
    return contacts
      .map(c => {
        const last = getLastMessage(c.id);

        return {
          ...c,
          lastMessage: last?.body ?? c.lastMessageText,
          lastMessageTime: last?.createdAt ?? c.lastMessageTime,
          unreadCount: getUnreadCount(c.id),
          lastMessageObj: last,
        };
      })
      .sort((a, b) => {
        const tA = toTimestamp(a.lastMessageObj || a);
        const tB = toTimestamp(b.lastMessageObj || b);
        return tB - tA; // DESC
      });
  }, [contacts, messages, getLastMessage, getUnreadCount]);

  const getChat = useCallback(
    (contactId: number) => chats.find(c => c.id === contactId) || null,
    [chats],
  );

  const [chatListContactMenu, setChatListContactMenu] = useState<IContact[]>(
    [],
  );
  useEffect(() => {
    setChatListContactMenu(chats);
  }, [chats]);

  return {
    chats,
    chatListContactMenu,
    bookmarkContact,
    getChat,
    getLastMessage,
    getUnreadCount,
    getMessagesByContact,
  };
};
